/**
 * El botón que ofrece la guía de la pantalla en la que estás.
 *
 * **Siempre visible mientras haya algo que enseñar**, se haya hecho ya la
 * guía o no. Antes desaparecía en cuanto estaban todas completadas, que es
 * justo cuando hace falta: nadie recuerda a la primera qué sella la primera
 * respuesta de un ticket, y sin botón no había forma de repasarlo — el
 * contexto de las guías no lo consumía ninguna otra pantalla.
 *
 * Lo único que lo oculta es que la guía esté corriendo: el velo ya tapa la
 * pantalla y el botón estorbaría.
 *
 * ## Vive en la cabecera, no flotando
 *
 * Antes era un botón flotante en la esquina inferior derecha, y **tapaba**: el
 * menú de acciones de la última fila de cada tabla, las barras de guardar
 * fijas al pie, y en general cualquier cosa que cayera en esa esquina a media
 * página. Se intentó dejarle sitio con un `padding` al final del contenido,
 * pero eso sólo despeja el final del scroll — el botón es `fixed` y flota
 * sobre el viewport, así que seguía encima de todo lo demás.
 *
 * En la cabecera el problema desaparece de raíz: ocupa su sitio en la fila de
 * acciones, junto a la campana, y no hay nada que pueda tapar. Es además donde
 * la gente busca la ayuda.
 *
 * **Ofrece la de esta vista.** Si la vista no tiene la suya —una subruta muy
 * adentro, una pantalla sin guía todavía— cae a la del panel en general, y
 * lo dice: prometer «esta pantalla» y abrir otra cosa es peor que no
 * ofrecer nada.
 */

import { motion, useReducedMotion } from 'framer-motion';
import { spring } from '@/lib/motion';
import { useTour } from '../TourContext';

export function TourLauncher() {
  const { flowForCurrentPath, suggestedFlow, completed, isRunning, startFlow } =
    useTour();
  const prefersReducedMotion = useReducedMotion();

  const flow = flowForCurrentPath ?? suggestedFlow;
  if (!flow || isRunning) return null;

  const esDeEstaVista = flowForCurrentPath !== undefined;
  const yaVista = completed.includes(flow.id);

  const etiqueta = esDeEstaVista
    ? yaVista
      ? 'Repasar esta pantalla'
      : `Guía: ${flow.title}`
    : `Guía: ${flow.title}`;

  const icono = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );

  return (
    <motion.button
      type="button"
      onClick={() => startFlow(flow.id)}
      initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
      animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
      transition={spring.gentle}
      whileTap={{ scale: 0.97 }}
      // En pantallas estrechas se queda en el icono: el título de una guía es
      // largo y en la cabecera compite con la búsqueda y la campana.
      className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-background-secondary px-2 py-2 text-[13px] font-medium text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:px-3 lg:py-1.5"
      aria-label={etiqueta}
      title={etiqueta}
      data-tour="admin.header.guide"
    >
      {icono}
      <span className="hidden max-w-[200px] truncate lg:inline">
        {etiqueta}
      </span>
    </motion.button>
  );
}
